import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Card, CardContent, FormControlLabel, Switch, TextField, Button, Alert } from "@mui/material";
import { AvailabilityService } from "../../services/availability.service";
import AvailabilityGrid from "../../components/availability/AvailabilityGrid";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../hooks/useAuth";
import dayjs from "dayjs";

const TimeManagementPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [isActive, setIsActive] = useState(true);
    const [slots, setSlots] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const fetchAvailability = useCallback(async () => {
        setLoading(true);
        try {
            // Should pass employeeId if not "me" based, but here we assume "me" context or pass user.id if needed by mock
            // MockDb.getAvailability takes (empId, date)
            const data = await AvailabilityService.getAvailability(user.id, date);
            setIsActive(data.isActive);
            setSlots(data.slots || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [user.id, date]);

    useEffect(() => {
        if (user?.id) {
            fetchAvailability();
        }
    }, [fetchAvailability, user?.id]);

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            await AvailabilityService.saveAvailability({
                employeeId: user.id,
                date,
                slots,
                isActive
            });
            setMessage({ type: 'success', text: 'Availability saved successfully!' });
        } catch (e) {
            setMessage({ type: 'error', text: 'Failed to save.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box maxWidth="md">
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>Employee Time Management</Typography>
            
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                         <TextField
                            label="Select Date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <FormControlLabel
                            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                            label={isActive ? "Status: Active" : "Status: Inactive"}
                        />
                    </Box>

                    {loading ? (
                        <Loader />
                    ) : (
                         <AvailabilityGrid 
                            slots={slots} 
                            onChange={setSlots} 
                            disabled={!isActive} 
                        />
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" size="large" onClick={handleSave} disabled={saving || loading}>
                            {saving ? "Saving..." : "Save Availability"}
                        </Button>
                    </Box>

                    {message && (
                        <Alert severity={message.type} sx={{ mt: 2 }}>{message.text}</Alert>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default TimeManagementPage;
